import { TaskOutcomeFieldsSchema, TaskTypeViewSchema } from "@erp/shared-types";
import { ProcedureReadinessIssueSchema } from "./tasks.contract";
import {
  ExportTaskTypeBundleInputSchema,
  ExportTaskTypeBundleResponseSchema,
  ImportTaskTypeBundleInputSchema,
  ImportTaskTypeBundleResponseSchema,
} from "./taskTypeBundle.contract";
import { oc } from "@orpc/contract";
import { JSON_SCHEMA_OUTPUT_REGISTRY } from "@orpc/zod/zod4";
import { z } from "zod";
import {
  ActivityActorSchema,
  ActivityFieldDiffSchema,
  activityPageInputShape,
  activityPageResponseSchema,
} from "./activityTimeline.contract";
import {
  SystemEntity,
  TASK_COMPLETION_MODES,
  TaskExpectedSubtaskSchema,
  TaskIntendedAssigneeSchema,
  TaskProcedureBindingSchema,
  TaskProcedureBindingsSchema,
  TaskTypeActionSchema,
  TaskTypeActionsSchema,
  TaskTypeDefaultAssigneeAllocationSchema,
  TaskTypeDefaultAssigneeInputShape,
  TaskTypeDefaultAssigneeTypeSchema,
  TaskTypeEscalationTargetInputShape,
  TaskTypeEscalationTargetTypeSchema,
  TASK_METRIC_AGING_BUCKETS,
  TASK_TYPE_METRIC_KINDS,
  TaskTypeMetricSchema,
  TaskTypeMetricsSchema,
  refineTaskTypeDefaultAssignee,
  refineTaskTypeEscalationTarget,
  type TaskIntendedAssignee,
} from "@erp/shared-types";

// Default-assignee kinds grow (`workflow` joined user / agent / user_group).
// x-extensible-enum tells oasdiff the set may grow in responses; requests
// keep the closed enum.
JSON_SCHEMA_OUTPUT_REGISTRY.add(TaskTypeDefaultAssigneeTypeSchema, {
  enum: undefined,
  "x-extensible-enum": TaskTypeDefaultAssigneeTypeSchema.options,
} as { enum: undefined });

// --- Response schema ---

type TaskTypeInputFieldWire = {
  fieldName: string;
  fieldType: string;
  label: string;
  isRequired: boolean;
  isMultiple: boolean;
  enumValues?: string[] | null;
  validationRules?: {
    min?: number;
    max?: number;
    pattern?: string;
    minLength?: number;
    maxLength?: number;
    precision?: number;
    scale?: number;
  } | null;
  referencedEntityType?: SystemEntity | null;
  children?: TaskTypeInputFieldWire[];
};

const TaskTypeInputFieldValidationRulesSchema = z
  .object({
    min: z.number().optional(),
    max: z.number().optional(),
    pattern: z.string().optional(),
    minLength: z.number().optional(),
    maxLength: z.number().optional(),
    precision: z.number().optional(),
    scale: z.number().optional(),
  })
  .nullable()
  .optional();

export const TaskTypeInputFieldSchema: z.ZodType<TaskTypeInputFieldWire> =
  z.lazy(() =>
    z.object({
      fieldName: z.string(),
      fieldType: z.string(),
      label: z.string(),
      isRequired: z.boolean(),
      isMultiple: z.boolean(),
      enumValues: z.array(z.string()).nullable().optional(),
      validationRules: TaskTypeInputFieldValidationRulesSchema,
      referencedEntityType: z.enum(SystemEntity).nullable().optional(),
      children: z.array(TaskTypeInputFieldSchema).optional(),
    }),
  );

export const TaskTypeResponseSchema = z.object({
  id: z.uuid(),
  organizationId: z.uuid(),
  key: z.string(),
  name: z.string(),
  description: z.string().nullable(),
  // Opt-in anchoring: tasks of this type attach to (and are unique per) one
  // entity of this type while open.
  anchorEntityType: z.enum(SystemEntity).nullable(),
  // Checklist type keys (stable wire shape for existing clients).
  expectedSubtaskTypeKeys: z.array(z.string()).nullable(),
  // Rich checklist (keys + optional intendedAssignee). Always normalized
  // objects when present; null when the type has no checklist.
  expectedSubtasks: z.array(TaskExpectedSubtaskSchema).nullable(),
  completionMode: z.enum(TASK_COMPLETION_MODES),
  // Core Enum whose option names are the allowed outcome vocabulary; null
  // falls back to DEFAULT_TASK_OUTCOMES (and makes the outcome optional).
  outcomeEnumId: z.uuid().nullable(),
  outcomeFields: TaskOutcomeFieldsSchema.nullable().optional(),
  // Declared custom actions. Lenient on output (no uniqueness refine) so a
  // hand-repaired row can never brick reads; writes validate strictly.
  actions: z.array(TaskTypeActionSchema).nullable(),
  // The type's default playbook (ordered SOP bindings). Lenient on output for
  // the same reason as `actions`; writes validate with the strict list schema.
  procedures: z.array(TaskProcedureBindingSchema).nullable(),
  // Optional default namespace: tasks created with this type and no explicit
  // namespace are placed (and numbered) in this namespace.
  taskNamespaceId: z.uuid().nullable(),
  // Summary of the assigned default namespace for display; null when unset
  // or when the referenced namespace has been archived.
  taskNamespace: z
    .object({ id: z.uuid(), name: z.string(), key: z.string() })
    .nullable(),
  // Optional default assignee applied to tasks created with this type and no
  // explicit assignee. The type discriminates which id column is in force;
  // `user_group` is a pool and the allocation picks one member per task.
  defaultAssigneeType: TaskTypeDefaultAssigneeTypeSchema.nullable(),
  defaultAssigneeUserId: z.uuid().nullable(),
  defaultAssigneeAgentDefinitionId: z.uuid().nullable(),
  defaultAssigneeUserGroupId: z.uuid().nullable(),
  defaultAssigneeWorkflowId: z.uuid().nullable(),
  defaultAssigneeAllocation: TaskTypeDefaultAssigneeAllocationSchema.nullable(),
  // Optional escalation target: who receives an agent's `needs_input` hand-off
  // on tasks of this type, ahead of the creator/triggerer/watcher cascade.
  // Humans only; `user_group` is a pool allocated per escalation.
  escalationTargetType: TaskTypeEscalationTargetTypeSchema.nullable(),
  escalationTargetUserId: z.uuid().nullable(),
  escalationTargetUserGroupId: z.uuid().nullable(),
  escalationTargetAllocation:
    TaskTypeDefaultAssigneeAllocationSchema.nullable(),
  // User groups whose members see every task of this type in the queue.
  // Empty keeps the personal scope (assignee, creator, or watcher).
  viewerUserGroupIds: z.array(z.uuid()),
  // Optional create-time input contract: a SchemaDefinition whose fields
  // callers of createTask must satisfy. Null = type accepts no typed input.
  inputSchemaDefinitionId: z.uuid().nullable(),
  // Hydrated schema summary for the picker / workflow socket generation.
  // Null when unset or the referenced definition has been archived.
  inputSchema: z
    .object({
      id: z.uuid(),
      name: z.string(),
      fields: z.array(TaskTypeInputFieldSchema),
    })
    .nullable(),
  // Saved layout spec. Lenient on read: a future component name returns the
  // raw JSON plus `viewError` instead of failing the task type fetch.
  // Optional so older serializers stay valid; writers always set both.
  view: z.unknown().nullable().optional(),
  viewError: z.string().nullable().optional(),
  // Metric cards shown above the queue on a Task Page. Null = the defaults
  // (open count, unassigned, aging). Lenient on read like `actions`.
  metrics: z.array(TaskTypeMetricSchema).nullable().optional(),
  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date(),
});

// Stable machine key referenced by workflows/services (e.g. "approve-invoice").
// Lowercase kebab-case, 1-100 chars.
export const TaskTypeKeySchema = z
  .string()
  .trim()
  .min(1)
  .max(100)
  .regex(
    /^[a-z0-9]+(?:-[a-z0-9]+)*$/,
    "Key must be lowercase kebab-case (e.g. 'approve-invoice')",
  );

/** Nested checklist depth the AI / apply path will persist. */
export const MAX_TASK_TYPE_STAGE_TREE_DEPTH = 4;
/** Hard cap on nodes in one generated/applied tree (parent's stages + nested). */
export const MAX_TASK_TYPE_STAGE_TREE_NODES = 40;

export type TaskTypeStageTreeNode = {
  key: string;
  name: string;
  description: string | null;
  intendedAssignee: TaskIntendedAssignee | null;
  stages: TaskTypeStageTreeNode[];
};

const TaskTypeStageTreeNodeBaseSchema = z.object({
  key: TaskTypeKeySchema,
  name: z.string().min(1).max(255),
  description: z.string().max(10000).nullable(),
  intendedAssignee: TaskIntendedAssigneeSchema.nullable(),
});

export const TaskTypeStageTreeNodeSchema: z.ZodType<TaskTypeStageTreeNode> =
  TaskTypeStageTreeNodeBaseSchema.extend({
    stages: z.lazy(() => z.array(TaskTypeStageTreeNodeSchema).max(50)),
  });

export const TaskTypeExistingTypeCatalogEntrySchema = z.object({
  key: TaskTypeKeySchema,
  name: z.string().min(1).max(255),
});

export const GenerateTaskTypeStageTreeInputSchema = z.object({
  prompt: z.string().trim().min(1).max(4000),
  parent: z.object({
    key: TaskTypeKeySchema,
    name: z.string().min(1).max(255),
    description: z.string().max(10000).nullable(),
  }),
  existingTypes: z
    .array(TaskTypeExistingTypeCatalogEntrySchema)
    .max(200)
    .optional(),
  /** Current checklist tree so the model can edit rather than start from scratch. */
  currentStages: z.array(TaskTypeStageTreeNodeSchema).max(50).optional(),
});

export const GenerateTaskTypeStageTreeResponseSchema = z.object({
  stages: z.array(TaskTypeStageTreeNodeSchema).min(1).max(50),
  explanation: z.string().max(2000),
});

export const ApplyTaskTypeStageTreeInputSchema = z.object({
  parentId: z.uuid(),
  stages: z.array(TaskTypeStageTreeNodeSchema).min(1).max(50),
});

// --- Input schemas ---

// Behavior fields shared by create/update. `null` clears a nullable field;
// omitting leaves it unchanged (update) or defaults it (create).
// `expectedSubtasks` is preferred when both checklist fields are sent.
const TaskTypeBehaviorInputShape = {
  anchorEntityType: z.enum(SystemEntity).nullable().optional(),
  expectedSubtaskTypeKeys: z
    .array(z.string().min(1))
    .max(50)
    .nullable()
    .optional(),
  expectedSubtasks: z
    .array(TaskExpectedSubtaskSchema)
    .max(50)
    .nullable()
    .optional(),
  completionMode: z.enum(TASK_COMPLETION_MODES).optional(),
  outcomeEnumId: z.uuid().nullable().optional(),
  outcomeFields: TaskOutcomeFieldsSchema.nullable().optional(),
  actions: TaskTypeActionsSchema.nullable().optional(),
  // Playbook bindings. null clears; `excluded` is rejected at type scope.
  procedures: TaskProcedureBindingsSchema.nullable().optional(),
  // Default namespace assignment. null clears it (update); omitting leaves it
  // unchanged (update) or unset (create). Only affects future task creates.
  taskNamespaceId: z.uuid().nullable().optional(),
  // Default assignee. `defaultAssigneeType: null` clears it (update); omitting
  // leaves it unchanged (update) or unset (create). Only affects future
  // task creates that name no explicit assignee.
  ...TaskTypeDefaultAssigneeInputShape,
  // Escalation target. `escalationTargetType: null` clears it (update);
  // omitting leaves it unchanged (update) or unset (create). Consulted when
  // an agent escalates a task of this type to needs_input.
  ...TaskTypeEscalationTargetInputShape,
  // Create-time input schema. null clears it (update); omitting leaves it
  // unchanged (update) or unset (create). Only affects future task creates.
  inputSchemaDefinitionId: z.uuid().nullable().optional(),
  // Layout spec. null clears it. A non-null value is rejected unless the
  // type has a live input schema and every path resolves against it.
  view: TaskTypeViewSchema.nullable().optional(),
  // Metric cards. null resets to the defaults; omitting leaves them unchanged
  // (update) or unset (create). A `sum` card must name a numeric input field.
  metrics: TaskTypeMetricsSchema.nullable().optional(),
  // Queue viewers. On update, a present array (including empty) replaces the
  // set; omitting leaves it unchanged. On create, omitting means no viewers.
  viewerUserGroupIds: z.array(z.uuid()).max(100).optional(),
} as const;

export const CreateTaskTypeInputSchema = z
  .object({
    key: TaskTypeKeySchema,
    name: z.string().min(1).max(255),
    description: z.string().max(10000).nullable().optional(),
    ...TaskTypeBehaviorInputShape,
  })
  .superRefine(refineTaskTypeDefaultAssignee)
  .superRefine(refineTaskTypeEscalationTarget);

// The key is immutable: workflows and getOrCreateByAnchor callers reference
// types by key, so re-keying would silently detach them.
export const UpdateTaskTypeInputSchema = z
  .object({
    id: z.uuid(),
    name: z.string().min(1).max(255).optional(),
    description: z.string().max(10000).nullable().optional(),
    ...TaskTypeBehaviorInputShape,
  })
  .superRefine(refineTaskTypeDefaultAssignee)
  .superRefine(refineTaskTypeEscalationTarget);

// --- Procedure health (task playbooks, read-only) ---

export const ProcedureHealthInputSchema = z.object({
  taskTypeId: z.uuid(),
  // Look-back window over tasks of this type, by creation date.
  days: z.int().min(1).max(365).prefault(30),
});

/** Health of one SOP bound on the task type, over the window's tasks. */
export const ProcedureHealthEntrySchema = z.object({
  sopId: z.uuid(),
  title: z.string(),
  mode: z.enum(["required", "suggested"]),
  // Recorded runs for the window's tasks (setup/teardown replays + inline run_procedure).
  runs: z.object({
    total: z.number().int(),
    succeeded: z.number().int(),
    failed: z.number().int(),
  }),
  // Operator tool pulls: get_procedure reads and run_procedure replays.
  pulls: z.object({ read: z.number().int(), replayed: z.number().int() }),
  // Distinct tasks in the window where the agent pulled this SOP at all.
  tasksWithPull: z.number().int(),
  // playbook_waived task actions naming this SOP.
  waivers: z.number().int(),
});

export const ProcedureHealthResponseSchema = z.object({
  taskTypeId: z.uuid(),
  days: z.number().int(),
  tasksInWindow: z.number().int(),
  truncated: z.boolean(),
  // Title-only replay calls omitted because multiple SOPs share the title.
  ambiguousPulls: z.int().min(0),
  entries: z.array(ProcedureHealthEntrySchema),
  // SOPs the agent pulled from its library that the type does not bind —
  // the strongest "this playbook is missing something" signal.
  unboundPulls: z.array(
    z.object({
      sopId: z.uuid().nullable(),
      name: z.string(),
      count: z.number().int(),
      tasksWithPull: z.number().int(),
    }),
  ),
});

// --- Metric cards (Task Page strip, read-only) ---

export const TaskMetricAgingBucketsSchema = z.object({
  "0-2": z.number().int(),
  "3-7": z.number().int(),
  "8-14": z.number().int(),
  "15+": z.number().int(),
}) satisfies z.ZodType<
  Record<(typeof TASK_METRIC_AGING_BUCKETS)[number], number>
>;

export const TaskTypeMetricCardSchema = z.object({
  kind: z.enum(TASK_TYPE_METRIC_KINDS),
  // The configured label; null means the client shows its default title.
  label: z.string().nullable(),
  // `sum` only: the input field it totals.
  field: z.string().optional(),
  // open_count / unassigned / sum.
  value: z.number().optional(),
  // aging only.
  buckets: TaskMetricAgingBucketsSchema.optional(),
});

export const TaskTypeMetricsResponseSchema = z.object({
  cards: z.array(TaskTypeMetricCardSchema),
});

// --- Contracts ---

/** A workflow that uses a task type: one of its steps names the type in its config. */
export const TaskTypeFeedSchema = z.object({
  workflowId: z.uuid(),
  name: z.string(),
  isActive: z.boolean(),
});

// ── Activity timeline (audit trail) ──────────────────────────────────────────

export const TaskTypeActivityKindSchema = z.enum([
  "created",
  "updated",
  "archived",
  "restored",
  "deleted",
  "viewers_changed",
]);

export const TaskTypeActivityUserGroupRefSchema = z.object({
  id: z.uuid(),
  /** Null when the group no longer exists. */
  name: z.string().nullable(),
});

export const TaskTypeActivityEntrySchema = z.object({
  id: z.string(),
  kind: TaskTypeActivityKindSchema,
  timestamp: z.iso.datetime(),
  actor: ActivityActorSchema,
  /** `updated`: column → { old, new }, bookkeeping columns left out. */
  changedFields: z.record(z.string(), ActivityFieldDiffSchema).optional(),
  /** `viewers_changed`: the groups one save added and removed. */
  viewerGroups: z
    .object({
      added: z.array(TaskTypeActivityUserGroupRefSchema),
      removed: z.array(TaskTypeActivityUserGroupRefSchema),
    })
    .optional(),
});

export const ListTaskTypeActivityRequestSchema = z.object({
  taskTypeId: z.uuid().describe("Task type whose audit trail to list"),
  ...activityPageInputShape,
});

export const ListTaskTypeActivityResponseSchema = activityPageResponseSchema(
  TaskTypeActivityEntrySchema,
);

export const taskTypesContracts = {
  /** Audit trail of a task type, read off `audit_change_log`; newest first. */
  listActivity: oc
    .input(ListTaskTypeActivityRequestSchema)
    .output(ListTaskTypeActivityResponseSchema)
    .route({
      summary: "List a task type's activity timeline",
      description:
        "Audit trail of a task type's configuration, newest first: creation, setting changes with field diffs, viewer group changes, archive and restore, each with the acting user. Archived types still resolve. Requires task type read permission.",
      method: "GET",
    }),
  listFeeds: oc
    .input(z.object({ taskTypeKey: z.string().min(1) }))
    .output(z.array(TaskTypeFeedSchema))
    .route({
      summary: "List a task type's linked workflows",
      description:
        "Returns the workflows whose current (published) version has a step whose config.taskTypeKey is this task type.",
    }),
  metrics: oc
    .input(
      z.object({
        taskTypeId: z.uuid(),
        // Match a queue that hides snoozed tasks.
        excludeSnoozed: z.boolean().optional(),
      }),
    )
    .output(TaskTypeMetricsResponseSchema)
    .route({
      summary: "Get task type metric cards",
      description:
        "Computes the task type's configured metric cards (open count, unassigned, aging buckets, field sums) over the open tasks of that type the caller can see.",
      method: "GET",
    }),
  getOutcomeOptions: oc
    .input(z.object({ enumId: z.uuid() }))
    .output(z.array(z.string()))
    .route({
      summary: "List task outcome options",
      description:
        "Returns the allowed outcome names defined by a task outcome enum; empty when the enum makes outcomes optional.",
    }),
  list: oc
    .input(
      z.object({
        // Archived (soft-deleted) types are left out unless asked for, e.g.
        // to read display settings for tasks that outlived their type.
        includeArchived: z.boolean().optional(),
      }),
    )
    .output(z.array(TaskTypeResponseSchema))
    .route({
      summary: "List task types",
      description:
        "Returns all task types configured for the organization, including checklist, completion mode, default assignee, escalation target, playbook and input schema settings. Pass includeArchived to also return archived types.",
      method: "GET",
    }),

  get: oc
    .input(z.object({ id: z.uuid() }))
    .output(TaskTypeResponseSchema.nullable())
    .route({
      summary: "Get task type",
      description:
        "Returns one task type by id with its full configuration, or null when it does not exist.",
      method: "GET",
    }),

  create: oc
    .input(CreateTaskTypeInputSchema)
    .output(TaskTypeResponseSchema)
    .route({
      summary: "Create task type",
      description:
        "Creates a task type with a unique kebab-case key, name, and optional checklist, outcomes, actions, playbook, default assignee, escalation target, viewers and input schema.",
      method: "POST",
    }),

  update: oc
    .input(UpdateTaskTypeInputSchema)
    .output(TaskTypeResponseSchema)
    .route({
      summary: "Update task type",
      description:
        "Updates a task type's name, description or behavior settings; the key cannot change. Default assignee, namespace and input schema changes only affect future tasks.",
      method: "PUT",
    }),

  archive: oc
    .input(z.object({ id: z.uuid() }))
    .output(z.object({ success: z.boolean() }))
    .route({
      summary: "Archive task type",
      description:
        "Soft-deletes a task type. Blocked while any open task still uses it; closed tasks keep their reference.",
      method: "DELETE",
    }),

  generateStageTree: oc
    .input(GenerateTaskTypeStageTreeInputSchema)
    .output(GenerateTaskTypeStageTreeResponseSchema)
    .route({
      summary: "Generate task checklist with AI",
      description:
        "Uses AI to draft or revise a nested checklist of stages for a task type from a prompt; nothing is saved.",
      method: "POST",
    }),

  applyStageTree: oc
    .input(ApplyTaskTypeStageTreeInputSchema)
    .output(TaskTypeResponseSchema)
    .route({
      summary: "Apply checklist stage tree to task type",
      description:
        "Saves a stage tree as the task type's checklist, creating missing child task types for stages. Requires task type create and update permission.",
      method: "POST",
    }),

  previewProcedureReadiness: oc
    .input(
      z.object({
        agentDefinitionId: z.uuid(),
        procedures: TaskProcedureBindingsSchema,
      }),
    )
    .output(
      z.object({
        agentName: z.string(),
        enabled: z.boolean(),
        issues: z.array(ProcedureReadinessIssueSchema),
      }),
    )
    .route({
      summary: "Preview playbook readiness for an agent",
      description:
        "Checks unsaved playbook procedure bindings against an agent and reports whether the agent is enabled and any issues that would block running them. Read-only.",
      method: "POST",
    }),

  exportBundle: oc
    .input(ExportTaskTypeBundleInputSchema)
    .output(ExportTaskTypeBundleResponseSchema)
    .route({
      summary: "Export task types as JSON",
      description:
        "Builds a portable JSON bundle of task types with the outcome enums, input schemas and checklist subtask types they depend on, pulled in recursively. Org-specific settings (assignees, escalation targets, viewers, playbooks) are left out and listed as warnings. Requires task type read permission.",
      method: "POST",
    }),

  importBundle: oc
    .input(ImportTaskTypeBundleInputSchema)
    .output(ImportTaskTypeBundleResponseSchema)
    .route({
      summary: "Import task types from JSON",
      description:
        "Imports a task type bundle. Each enum and schema reuses an equivalent existing one or is created; task types whose key exists are skipped or overwritten. dryRun reports the plan without writing. Requires task type create permission, plus update to overwrite.",
      method: "POST",
    }),

  getProcedureHealth: oc
    .input(ProcedureHealthInputSchema)
    .output(ProcedureHealthResponseSchema)
    .route({
      summary: "Get task type playbook health",
      description:
        "Reports how each playbook procedure was run, read, replayed or waived across the task type's tasks over recent days, plus procedures agents used outside the playbook.",
      method: "GET",
    }),
} as const;

// --- Type exports ---

export type TaskTypeResponse = z.infer<typeof TaskTypeResponseSchema>;
export type CreateTaskTypeInput = z.infer<typeof CreateTaskTypeInputSchema>;
export type UpdateTaskTypeInput = z.infer<typeof UpdateTaskTypeInputSchema>;
export type GenerateTaskTypeStageTreeInput = z.infer<
  typeof GenerateTaskTypeStageTreeInputSchema
>;
export type GenerateTaskTypeStageTreeResponse = z.infer<
  typeof GenerateTaskTypeStageTreeResponseSchema
>;
export type ApplyTaskTypeStageTreeInput = z.infer<
  typeof ApplyTaskTypeStageTreeInputSchema
>;
export type TaskTypeExistingTypeCatalogEntry = z.infer<
  typeof TaskTypeExistingTypeCatalogEntrySchema
>;
export type TaskTypeMetricCard = z.infer<typeof TaskTypeMetricCardSchema>;
export type TaskMetricAgingBuckets = z.infer<
  typeof TaskMetricAgingBucketsSchema
>;
export type TaskTypeMetricsResponse = z.infer<
  typeof TaskTypeMetricsResponseSchema
>;
export type TaskTypeActivityKind = z.infer<typeof TaskTypeActivityKindSchema>;
export type TaskTypeActivityUserGroupRef = z.infer<
  typeof TaskTypeActivityUserGroupRefSchema
>;
export type TaskTypeActivityEntry = z.infer<typeof TaskTypeActivityEntrySchema>;
export type ListTaskTypeActivityResponse = z.infer<
  typeof ListTaskTypeActivityResponseSchema
>;
export type ProcedureHealthInput = z.infer<typeof ProcedureHealthInputSchema>;
export type ProcedureHealthEntry = z.infer<typeof ProcedureHealthEntrySchema>;
export type ProcedureHealthResponse = z.infer<
  typeof ProcedureHealthResponseSchema
>;
